import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import './slide.scss'

import { graphql } from 'gatsby'
import React, { useEffect, useRef } from 'react'
import { isMobile } from 'react-device-detect'
import Slider, { type Settings } from 'react-slick'

import Link from '@/components/link/link'
import ResponsiveImage from '@/components/responsive-image/responsive-image'
import { resolveCtaUrl } from '@/internal/utils/resolve-cta-link-url'

import SlideVideo from './slide-video'

type Props = {
  slides: Queries.SlideFragment[]
}
const Slide: React.FC<Props> = ({ slides }) => {
  const slider = useRef<Slider>(null)

  useEffect(() => {
    const secondSlide = document?.querySelector('.slick-slide:nth-child(2)')
    if (secondSlide) {
      secondSlide.classList.add('no-animation')
    }
  }, [])

  const beforeChange = (prev: number, next: number): void => {
    if (slider.current !== null) {
      const sliderCurrent = slider.current
      const list = sliderCurrent.innerSlider?.list

      if (list) {
        const prevSlideElement = list.querySelector(`[data-index="${prev}"]`)
        const nextSlideElement = list.querySelector(`[data-index="${next}"]`)

        if (prevSlideElement && nextSlideElement) {
          setTimeout(() => {
            prevSlideElement.classList.remove('next-slide-anim')
            nextSlideElement.classList.add('next-slide-anim')
          }, 100)

          const currentSlideVideo = prevSlideElement.querySelector('video')
          if (currentSlideVideo) {
            currentSlideVideo.pause()
          }
        }
      }
    }
  }
  const afterChange = async (currentSlide: number): Promise<void> => {
    const sliderCurrent = slider.current
    if (sliderCurrent) {
      const currentSlideElement =
        sliderCurrent?.innerSlider?.list?.querySelector(
          `[data-index="${currentSlide}"]`
        )

      if (currentSlideElement) {
        const currentSlideVideo = currentSlideElement.querySelector('video')
        if (currentSlideVideo?.paused === true) {
          currentSlideVideo.currentTime = 0
          await currentSlideVideo.play()
        }
      }
    }
  }

  const customPaging = (i: number): JSX.Element => {
    return <a>{slides[i].navigationText}</a>
  }

  const settings: Settings = {
    customPaging,
    className: 'hero__carousel',
    // onInit,
    afterChange: (currentSlide: number) => {
      afterChange(currentSlide).catch((error) => {
        console.error(error)
      })
    },
    beforeChange,
    lazyLoad: 'progressive',
    infinite: true,
    fade: false,
    dots: true,
    speed: 100,
    slidesToShow: 1,
    slidesToScroll: 1,
    useCSS: false,
    autoplaySpeed: 1,
    draggable: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          fade: false
        }
      }
    ]
  }

  return (
    <div className="hero--homepage">
      <Slider {...settings} ref={slider} data-cy="slider">
        {slides.map((slide, index) => {
          if (slide === null) {
            return null
          }
          const cta = slide?.cta?.[0]
          const url = cta ? resolveCtaUrl(cta) : ''
          return (
            <div key={slide.id}>
              <div className="hero__slide row column">
                <Link
                  to={url}
                  className="hero__full-width-link"
                  data-cy="slide-content-link"
                >
                  {' '}
                </Link>
                <Link
                  to={url}
                  target=""
                  className="hero__text-content large-5 column"
                >
                  <h2 data-cy="slide-title" className="hero__title">
                    {slide.title}
                  </h2>
                  {cta && (
                    <div className="hero__cta ">
                      <span className="hero__cta-text">{cta.text}</span>
                      <span className="icon-long-arrow-right" />
                    </div>
                  )}
                </Link>
                <div className="hero__image center">
                  <div className="hero__overlay" />

                  {slide?.image?.desktop && (
                    <ResponsiveImage
                      image={slide.image?.desktop}
                      mobile={slide.imageVertical?.image ?? slide.image?.mobile}
                      alt={slide.title}
                      loading="eager"
                    />
                  )}

                  {slide?.image?.video && (
                    <SlideVideo
                      isFirst={index === 0}
                      isMobile={isMobile}
                      desktop={slide.image.video}
                      mobile={slide.imageVertical?.video}
                    />
                  )}
                </div>
                <div className="mobile-block">
                  <div className="content">
                    <Link to={url}>
                      <h2 data-cy="slide-title-mobile" className="hero__title">
                        {slide.title}
                      </h2>
                      {cta && (
                        <div className="hero__cta">
                          <span className="hero__cta-text">{cta.text}</span>
                          <span className="icon-long-arrow-right" />
                        </div>
                      )}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </Slider>
    </div>
  )
}

export default Slide

export const query = graphql`
  fragment Slide on DatoCmsSlide {
    id
    __typename
    title
    navigationText
    image {
      desktop: gatsbyImageData(
        width: 1920
        height: 1080
        imgixParams: { w: "1920", h: "1080", fit: "crop", crop: "focalpoint" }
      )
      mobile: gatsbyImageData(
        width: 780
        height: 900
        imgixParams: { w: "780", h: "900", fit: "crop", crop: "focalpoint" }
      )
      video {
        mp4Url
        thumbnailUrl
        mp4High: mp4Url(res: high)
        mp4Med: mp4Url(res: medium)
        mp4Low: mp4Url(res: low)
      }
    }
    imageVertical {
      video {
        mp4Url
        thumbnailUrl
        mp4High: mp4Url(res: high)
        mp4Med: mp4Url(res: medium)
        mp4Low: mp4Url(res: low)
      }
      image: gatsbyImageData(
        width: 780
        height: 900
        imgixParams: { w: "780", h: "900", fit: "crop", crop: "focalpoint" }
      )
    }
    cta {
      __typename
      ... on DatoCmsCta {
        ...Cta
      }
    }
  }
`
